// Settings → Audiences: the chip list for this community. Who may receive what, one list used by
// every knowledge picker (and, in Phase 2, by Broadcasts). Managers and above edit it.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Briefcase, Plus, Pencil, Trash2, Lock, Globe, Eye, Loader2, X, Check, KeyRound } from "lucide-react";
import { useDash } from "@/pages/dashboard/ctx";
import { useAudiences, OPEN } from "@/lib/audiences";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { V } from "@/lib/vocab";
import { SettingsSection } from "@/components/SettingsSection";

const inputCls = "w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

function Row({ a, canEdit, onRename, onDelete, onViewAs, onVerifyDefault }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(a.label);
  const auto = a.kind === "auto";
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-t border-sand-100" data-testid={`audience-row-${a.key}`}>
      <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${a.group === "team" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"}`}>
        {a.group === "team" ? <Briefcase className="h-4 w-4" /> : <Users className="h-4 w-4" />}
      </span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input data-testid={`audience-rename-${a.key}`} className={inputCls + " max-w-[16rem]"} value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
            <button data-testid={`audience-rename-save-${a.key}`} onClick={async () => { await onRename(a, label); setEditing(false); }} className="rounded-full bg-ink text-white p-1.5"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => { setLabel(a.label); setEditing(false); }} className="rounded-full border border-sand-200 p-1.5"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-sm truncate" data-testid={`audience-label-${a.key}`}>{a.label}</span>
            <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 bg-sand-100 text-ink/50">{auto ? "automatic" : a.kind === "rule" ? "rule" : "hand-picked"}</span>
            {a.key === "staff" && <span className="text-[10px] text-ink/50">hears everything</span>}
          </div>
        )}
        <div className="text-xs text-muted-foreground truncate">{a.description || (a.group === "team" ? "Team logins" : "Roster people")}</div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="text-sm font-semibold tabular-nums" data-testid={`audience-members-${a.key}`}>{a.members}</div>
        <div className="text-[10px] uppercase tracking-wide text-ink/40">{a.members === 1 ? "person" : "people"}</div>
      </div>
      <div className="text-right shrink-0 hidden sm:block w-16">
        <div className="text-sm font-semibold tabular-nums" data-testid={`audience-items-${a.key}`}>{a.items}</div>
        <div className="text-[10px] uppercase tracking-wide text-ink/40">items</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {a.key !== "staff" && (
          <button data-testid={`audience-verify-default-${a.key}`} onClick={() => canEdit && onVerifyDefault(a, !a.verify_default)} disabled={!canEdit}
            aria-pressed={!!a.verify_default}
            title={a.verify_default ? "Identity check by default is ON — items shared only with this chip stay hidden until the caller passes the texted-code check" : "Turn on identity check by default for items shared only with this chip"}
            className={`rounded-full border p-1.5 transition-colors ${a.verify_default ? "border-amber-400 bg-amber-50 text-amber-700" : "border-sand-200 text-ink/40 hover:border-ink hover:text-ink"} disabled:opacity-60`}>
            <KeyRound className="h-3.5 w-3.5" />
          </button>
        )}
        <button data-testid={`audience-view-as-${a.key}`} onClick={() => onViewAs(a)} title="See what a caller with only this chip would receive" className="rounded-full border border-sand-200 p-1.5 hover:border-ink"><Eye className="h-3.5 w-3.5" /></button>
        {canEdit && <button data-testid={`audience-edit-${a.key}`} onClick={() => setEditing(true)} title="Rename" className="rounded-full border border-sand-200 p-1.5 hover:border-ink"><Pencil className="h-3.5 w-3.5" /></button>}
        {canEdit && !a.builtin && <button data-testid={`audience-delete-${a.key}`} onClick={() => onDelete(a)} title="Remove" className="rounded-full border border-sand-200 p-1.5 hover:border-redline-500 hover:text-redline-600"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export function ViewAsPanel({ tenantId, chipKey, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (!chipKey) return;
    api.get(`/tenants/${tenantId}/audiences/view-as`, { params: { key: chipKey } }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load the preview"));
  }, [tenantId, chipKey]);
  return (
    <Dialog open={!!chipKey} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="view-as-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Eye className="h-4 w-4" /> View as: {data?.label || "…"}</DialogTitle>
          <DialogDescription>What the AI may draw on when someone carrying only this chip calls or texts.</DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking every document, fact and rule…</div> : (
          <div className="space-y-3 text-sm">
            {data.sees_everything ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-800" data-testid="view-as-everything">Managers hear everything — nothing is held back from this chip.</div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2" data-testid="view-as-visible">
                  {Object.entries(data.visible).map(([k, n]) => <span key={k} className="rounded-full bg-sand-100 px-2.5 py-1 text-xs font-semibold">{n} {k}{n === 1 ? "" : "s"} shared</span>)}
                  {Object.keys(data.visible).length === 0 && <span className="text-muted-foreground">Nothing is shared with this chip yet.</span>}
                </div>
                <div className="rounded-xl border border-sand-200 overflow-hidden">
                  <div className="px-3 py-2 bg-sand-50 text-xs font-bold uppercase tracking-wide text-ink/60 flex items-center gap-1.5"><Lock className="h-3 w-3" /> Held back ({data.hidden_total})</div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-sand-100" data-testid="view-as-hidden">
                    {data.hidden.length === 0 && <div className="px-3 py-3 text-muted-foreground">Nothing is held back from this chip.</div>}
                    {data.hidden.map((h) => (
                      <div key={h.id} className="px-3 py-2 flex items-center justify-between gap-2">
                        <span className="truncate"><span className="text-[10px] uppercase tracking-wide text-ink/40 mr-1.5">{h.kind}</span>{h.title}</span>
                        <span className="text-[11px] text-ink/50 shrink-0">for {h.audiences.join(", ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AudiencesCard() {
  const { tenantId, role } = useDash();
  const canEdit = role !== "board_member";
  const aud = useAudiences(tenantId);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [group, setGroup] = useState("people");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewAs, setViewAs] = useState("");
  const [deleting, setDeleting] = useState(null); // {a, used}
  const [retag, setRetag] = useState("");

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/audiences`, { label, group, description: desc });
      toast.success(`“${label}” added — it's now a chip on every picker`);
      setLabel(""); setDesc(""); setAdding(false);
      aud.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add"); }
    finally { setBusy(false); }
  };
  const rename = async (a, newLabel) => {
    try { await api.put(`/tenants/${tenantId}/audiences/${a.key}`, { label: newLabel }); toast.success("Renamed"); aud.reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't rename"); }
  };
  const verifyDefault = async (a, on) => {
    try {
      await api.put(`/tenants/${tenantId}/audiences/${a.key}`, { verify_default: on });
      toast.success(on ? `“${a.label}”: identity check required by default` : `“${a.label}”: identity check default off`);
      aud.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  const del = async (a, retagTo = "") => {
    try {
      await api.delete(`/tenants/${tenantId}/audiences/${a.key}`, { params: retagTo ? { retag_to: retagTo } : {} });
      toast.success(`“${a.label}” removed`); setDeleting(null); setRetag(""); aud.reload();
    } catch (e) {
      if (e?.response?.status === 409) { setDeleting({ a, used: a.items, detail: e.response.data.detail }); return; }
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <SettingsSection id="audiences-card" icon={Lock} tone="violet" title="Audience groups — who the AI may share things with"
      badges={[{ text: `${aud.list.length} group${aud.list.length === 1 ? "" : "s"}` }]}
      summary={`The groups you pick from when you upload a document or add a fact (${aud.people.map((a) => a.label).join(", ")}${aud.team.length ? ` · ${aud.team.map((a) => a.label).join(", ")}` : ""}). Rename them, add your own, or set which ones require an identity check.`}
      headerRight={canEdit && !adding && aud.list.length < aud.max ? (
        <button data-testid="audience-add" onClick={() => setAdding(true)} className="rounded-full bg-ink text-white px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"><Plus className="h-3.5 w-3.5" /> Add audience</button>
      ) : null}>
      <p className="text-sm text-muted-foreground -mt-1">
        These are the “who can the AI share this with?” choices you see on every document, quick fact, announcement, contact and rule. A resident belongs to a group through their roster record (e.g. Owners, Board); team logins get theirs on the Team page. The AI only shares an item with someone who belongs to one of its groups.
        <span className="inline-flex items-center gap-1 ml-1 text-emerald-700 font-semibold"><Globe className="h-3 w-3" /> Anyone</span> is always available and means “no restriction”.
        <span className="inline-flex items-center gap-1 ml-1 text-amber-700 font-semibold"><KeyRound className="h-3 w-3" /> Key</span> = identity check by default for items shared only with that chip.
      </p>
      {adding && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4 grid gap-3 md:grid-cols-[1fr_auto_1fr_auto]" data-testid="audience-add-form">
          <input data-testid="audience-add-label" className={inputCls} placeholder="Name — e.g. Pool committee, Crew, Building B" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
          <select data-testid="audience-add-group" className={inputCls + " md:w-44"} value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="people">People (roster)</option>
            <option value="team">Team (logins)</option>
          </select>
          <input data-testid="audience-add-desc" className={inputCls} placeholder="What it's for (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
          <div className="flex items-center gap-1.5">
            <button data-testid="audience-add-save" disabled={busy || label.trim().length < 2} onClick={create} className="rounded-full bg-ink text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</button>
            <button onClick={() => setAdding(false)} className="rounded-full border border-sand-200 px-3 py-2 text-sm">Cancel</button>
          </div>
          <p className="md:col-span-4 text-[11px] text-ink/60">Hand-picked chips: you decide who's in. Owner/renter, {V.grp()} and managers fill in automatically. Messages, portal pages and the phone line all use these same chips.</p>
        </div>
      )}
      <div className="rounded-xl border border-sand-200 overflow-hidden -mx-1 sm:mx-0">
        <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-ink/40 flex items-center gap-1.5"><Users className="h-3 w-3" /> People</div>
        {aud.people.map((a) => <Row key={a.key} a={a} canEdit={canEdit} onRename={rename} onDelete={(x) => del(x)} onViewAs={(x) => setViewAs(x.key)} onVerifyDefault={verifyDefault} />)}
        <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-ink/40 flex items-center gap-1.5 border-t border-sand-100"><Briefcase className="h-3 w-3" /> Team</div>
        {aud.team.map((a) => <Row key={a.key} a={a} canEdit={canEdit} onRename={rename} onDelete={(x) => del(x)} onViewAs={(x) => setViewAs(x.key)} onVerifyDefault={verifyDefault} />)}
        <div className="px-4 py-2.5 border-t border-sand-100 text-xs text-ink/60 flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-emerald-600" /> Open to anyone: <b data-testid="audience-open-items">{aud.openItems}</b> items
          <button data-testid="audience-view-as-anyone" onClick={() => setViewAs(OPEN)} className="ml-auto rounded-full border border-sand-200 px-3 py-1 text-xs font-semibold hover:border-ink inline-flex items-center gap-1"><Eye className="h-3 w-3" /> View as a stranger</button>
        </div>
      </div>

      <ViewAsPanel tenantId={tenantId} chipKey={viewAs} onClose={() => setViewAs("")} />

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="audience-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Remove “{deleting?.a?.label}”?</DialogTitle>
            <DialogDescription>{deleting?.detail} Nothing is ever silently opened up — pick where those items go.</DialogDescription>
          </DialogHeader>
          <select data-testid="audience-retag-select" className={inputCls} value={retag} onChange={(e) => setRetag(e.target.value)}>
            <option value="">Move items to…</option>
            {aud.list.filter((x) => x.key !== deleting?.a?.key).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            <option value={OPEN}>Anyone (open them up)</option>
          </select>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleting(null)} className="rounded-full border border-sand-200 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="audience-retag-confirm" disabled={!retag} onClick={() => del(deleting.a, retag)} className="rounded-full bg-redline-500 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">Move items and remove</button>
          </div>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
